import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  attachFilesKeys,
  useGetAttachableDocumentsQuery,
  usePersistApprovalAttachmentsMutation,
  hasGeneratingScorecard,
  type AttachableDocumentsResponse,
  type AttachableGroup,
  type AttachableItem,
  type SelectionRef,
  type ScorecardStatus,
} from './attachFilesApi';
import { offerDetailsKeys } from '../../api';

export interface SelectedAttachment extends SelectionRef {
  filename: string;
  // contract.yml seam_1 — always present on the source AttachableItem now, so
  // always present here too (see attachFilesApi.ts's AttachableItem comment).
  content_type: string;
  byte_size: number;
  // AMENDMENT 1 — always present on the source AttachableItem now, so it's
  // always present here too. Drives the chip/picker-row preview (case N5).
  url: string;
  // Scorecards only; every other ref_type is implicitly 'ready'. Carried onto the
  // chip so it can show the post-Add loader in place of preview/✕.
  status?: ScorecardStatus;
}

// ref_id is nullable (scorecards/resume are synthetic rows, not blobs) so the
// identity key needs a stable placeholder for null.
export const attachmentKey = (ref: SelectionRef): string =>
  `${ref.ref_type}:${ref.ref_id ?? ''}`;

const toSelectedAttachment = (item: AttachableItem): SelectedAttachment => ({
  ref_type: item.ref_type,
  ref_id: item.ref_id,
  filename: item.filename,
  content_type: item.content_type,
  byte_size: item.byte_size,
  url: item.url,
  status: item.status,
});

// Selections are references (contract.yml "Architecture" note), so this hook
// never stores a blob or copies content — only ref_type/ref_id + the display
// metadata the server already resolved for the current groups.
export const useOfferAttachments = (offerId?: string | number) => {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, refetch } =
    useGetAttachableDocumentsQuery(offerId);

  // What the chips render + what a freshly-opened picker starts from — the
  // last state confirmed with the server, whether that's this session's
  // "Add" or a prior session's save-as-draft (L1 re-entry).
  const [confirmed, setConfirmed] = useState<Map<string, SelectedAttachment>>(
    new Map()
  );
  // What the open picker's checkboxes reflect — edited freely, discarded on
  // Cancel, only reaches `confirmed` via an explicit Add.
  const [draft, setDraft] = useState<Map<string, SelectedAttachment>>(
    new Map()
  );

  // Seeded exactly once per fetched response — tracked so a fresh `data`
  // reference (new fetch / refetch) reseeds, but re-renders in between don't.
  const [seededFrom, setSeededFrom] = useState<
    AttachableDocumentsResponse | undefined
  >(undefined);

  // The server's `selected` flags ARE the current truth. Only offer documents are
  // pre-checked (amendment_12), so on a new offer everything else is false; on a
  // reopened draft they are what was saved last time (L1). Adjusted during
  // render (React's documented pattern for syncing a prop/query result into
  // state) rather than in an effect, so there's no extra render in between.
  if (data && data !== seededFrom) {
    const next = new Map<string, SelectedAttachment>();
    (data.groups ?? []).forEach((group: AttachableGroup) => {
      group.items.forEach((item) => {
        // contract.yml amendment_7b: `selected: true` and `disabled: true` must
        // never co-occur server-side — but a disabled row must never seed a
        // chip regardless, so this is a defensive second check, not a trust
        // exercise. A candidate with no resume (or an offer whose scorecard
        // pre-generation hasn't landed yet) is exactly the case that produced
        // an untickable "ghost" chip before this guard existed.
        if (item.selected && !item.disabled) {
          next.set(attachmentKey(item), toSelectedAttachment(item));
        } else if (
          item.status === 'generating' &&
          confirmed.has(attachmentKey(item))
        ) {
          // A generating row is disabled and `selected: false` server-side, so
          // the guard above drops it — and the chip the recruiter just added
          // vanished for the whole 3-15s render before coming back. It is only
          // in flight BECAUSE they picked it, so hold it and let
          // SelectedAttachmentChips render its loader.
          next.set(attachmentKey(item), toSelectedAttachment(item));
        }
      });
    });
    setSeededFrom(data);
    // `confirmed` (chips) always tracks the server's own truth — replace it
    // outright.
    setConfirmed(next);
    // `draft` (the open picker's checkboxes) does NOT get replaced outright.
    // A refetch can land while the picker is open and mid-edit — most
    // notably the invalidate this same file fires right after "Upload new
    // document" — and a plain replace silently discarded every tick the
    // recruiter made before that refetch resolved (production bug: an
    // upload wiped unsaved selections with no toast, no cue). Instead, reapply
    // the LOCAL delta (relative to the previous `confirmed`, i.e. what the
    // recruiter changed since the picker last agreed with the server) on top
    // of the fresh `next`:
    //   - a key in the old draft but not the old confirmed is a local
    //     addition (a fresh tick, or `addUploadedToDraft`'s just-uploaded
    //     file) — keep it even though `next` doesn't know about it yet.
    //   - a key in the old confirmed but no longer in the old draft is a
    //     local removal — stay removed rather than have the server's
    //     `selected: true` snap it back on.
    // On a clean reopen (draft already == confirmed, nothing to reconcile)
    // this reduces to the old plain-replace behaviour, so H2/L1/first-open
    // are unaffected.
    //
    // A functional updater — reading `prevDraft` from the callback instead of
    // closing over the outer `draft` — so this composes correctly regardless
    // of whether this reseed or `addUploadedToDraft`'s own queued update
    // lands first in the same React flush; whichever runs second builds on
    // whatever the first produced, instead of one plain-value write clobbering
    // the other.
    setDraft((prevDraft) => {
      const merged = new Map(next);
      prevDraft.forEach((value, key) => {
        if (!confirmed.has(key)) merged.set(key, value);
      });
      confirmed.forEach((_value, key) => {
        if (!prevDraft.has(key)) merged.delete(key);
      });
      return merged;
    });
  }

  // Destructured: `useMutation` hands back a new object every render, so
  // depending on the whole thing below made `persist` — and every callback
  // built on it — churn identity on each render. `mutateAsync` is stable.
  const { mutateAsync: persistAttachments, isPending: isSaving } =
    usePersistApprovalAttachmentsMutation();

  const isDraftSelected = useCallback(
    (item: SelectionRef) => draft.has(attachmentKey(item)),
    [draft]
  );

  const toggleDraft = useCallback((item: AttachableItem) => {
    setDraft((prev) => {
      const next = new Map(prev);
      const key = attachmentKey(item);
      if (next.has(key)) next.delete(key);
      else next.set(key, toSelectedAttachment(item));
      return next;
    });
  }, []);

  // A freshly uploaded file (picker's "Upload new document") — not part of
  // any fetched group yet, so it's added straight into the draft.
  // A file that has been uploaded but that the server's attachable-documents
  // response has not caught up with yet. The PUT returns 201 before the blob is
  // listed, so the refetch fired right after it can come back without the new
  // file — leaving it ticked in `draft` with no row in `groups` to tick, which
  // reads as the upload having silently failed.
  const [pendingUploads, setPendingUploads] = useState<
    Map<string, SelectedAttachment>
  >(new Map());

  // The file the recruiter just picked, before the server has said anything
  // about it. Held here rather than in the picker because the chip that shows
  // it lives outside the popover — and the popover unmounts as soon as it
  // closes, which is the moment the upload is still running.
  const [uploadingFile, setUploadingFile] = useState<{
    filename: string;
    byte_size: number;
  } | null>(null);

  const addUploadedToDraft = useCallback((attachment: SelectedAttachment) => {
    setDraft((prev) => {
      const next = new Map(prev);
      next.set(attachmentKey(attachment), attachment);
      return next;
    });
    setPendingUploads((prev) => {
      const next = new Map(prev);
      next.set(attachmentKey(attachment), attachment);
      return next;
    });
  }, []);

  // Discard unsaved picker edits (Cancel) — reverts to the last confirmed set.
  const resetDraft = useCallback(() => setDraft(confirmed), [confirmed]);

  const persist = useCallback(
    async (next: Map<string, SelectedAttachment>) => {
      if (!offerId) return;
      const selections: SelectionRef[] = Array.from(next.values()).map(
        ({ ref_type, ref_id }) => ({ ref_type, ref_id })
      );
      const response = await persistAttachments({
        offerId,
        selections,
      });
      // `next` is already deduped by attachmentKey, so its size should always
      // equal core's own dedupe count (ApprovalAttachmentSelector#call). A
      // mismatch means the server's view of what got saved differs from what
      // we're about to render as confirmed — don't trust the local optimistic
      // state as if it were a confirmed success; force a refetch of the real
      // server state instead of silently rendering chips nothing backs.
      if (response?.count !== next.size) {
        await refetch();
        throw new Error('err_approval_attachments_count_mismatch');
      }
      setConfirmed(next);
      setDraft(next);
      // The offer show payload's `approval_attachments` (contract.yml
      // AMENDMENT 2) is served from a separate cache — without this, a
      // recruiter who saves here and immediately opens the approval preview
      // sees whatever was cached before this save.
      queryClient.invalidateQueries({
        queryKey: offerDetailsKeys.offerById(offerId),
      });
      // Saving the selection is what starts the scorecard render, so the row's status
      // only changes server-side. Without this the row stays 'pending' here — no loader,
      // and nothing to start the poll. Invalidated rather than awaited: the caller only
      // needs the save confirmed, and an awaited refetch here re-enters render
      // synchronously from inside the mutation.
      queryClient.invalidateQueries({
        queryKey: attachFilesKeys.byOffer(offerId),
      });
    },
    [offerId, persistAttachments, queryClient, refetch]
  );

  // Picker "Add" — persists whatever is currently drafted, including an
  // empty set (V1: deselect everything, still a valid save).
  const confirmDraft = useCallback(() => persist(draft), [persist, draft]);

  // Upload commit. The picker closes the moment a file is chosen, so there is
  // no "Add" left to press — but committing the whole draft would save every
  // unsaved tick and untick along with it, which is the exact bug the draft
  // reconciliation above exists to prevent. So only the uploaded file joins the
  // confirmed set, and the recruiter's in-progress edits are restored
  // afterwards (persist replaces the draft with whatever it saved).
  const commitUploaded = useCallback(
    async (attachment: SelectedAttachment) => {
      const key = attachmentKey(attachment);
      const nextConfirmed = new Map(confirmed);
      nextConfirmed.set(key, attachment);
      const draftBefore = new Map(draft);
      await persist(nextConfirmed);
      draftBefore.set(key, attachment);
      setDraft(draftBefore);
    },
    [confirmed, draft, persist]
  );

  // Chip ✕ — deselect ONLY. Removes the one ref from the confirmed set and
  // PUTs the remaining set; never calls a delete/purge endpoint (contract
  // seam_2 MUST_NOT — the underlying blob must survive).
  const removeConfirmed = useCallback(
    (ref: SelectionRef) => {
      const next = new Map(confirmed);
      next.delete(attachmentKey(ref));
      return persist(next);
    },
    [confirmed, persist]
  );

  const selectedAttachments = useMemo(
    () => Array.from(confirmed.values()),
    [confirmed]
  );

  // What's currently checked in an OPEN picker — including a just-uploaded
  // file, which `groups` doesn't know about yet. Drives the size guard (V3):
  // `byte_size` is already on every ref, so summing it is the whole guard.
  const draftAttachments = useMemo(() => Array.from(draft.values()), [draft]);

  // AMENDMENT 9 (D-E) — true while the scorecards row is mid-render. The
  // picker's loader reads `status` per-row directly off `groups`; this is
  // for the ONE consumer outside the picker that needs a single boolean:
  // the "Send offer for approval" footer button, which lives outside this
  // component tree entirely (OfferV1/index.tsx) and gates on it via the
  // shared offer store (see OfferApproval.tsx's effect).
  const isScorecardGenerating = useMemo(
    () => hasGeneratingScorecard(data),
    [data]
  );

  // Rows the picker renders: the server's own groups, plus any upload it has
  // not listed yet. Derived rather than held in state, so an upload drops out
  // the moment the server does list it — no reconciliation, and no way for a
  // stale local row to outlive the real one.
  const groups = useMemo(() => {
    const serverGroups = data?.groups ?? [];
    if (pendingUploads.size === 0) return serverGroups;

    const known = new Set<string>();
    serverGroups.forEach((group: AttachableGroup) =>
      group.items.forEach((item) => known.add(attachmentKey(item)))
    );
    const orphans = Array.from(pendingUploads.values()).filter(
      (upload) => !known.has(attachmentKey(upload))
    );
    if (orphans.length === 0) return serverGroups;

    // An uploaded document resolves server-side as an `offer_document`, which
    // the API lists under "other files" — so that is where it belongs while it
    // waits, rather than at the end of whichever group happens to be last.
    const asItems: AttachableItem[] = orphans.map((upload) => ({
      ...upload,
      selected: true,
      disabled: false,
    }));
    const target = serverGroups.find(
      (group: AttachableGroup) => group.key === 'other_files'
    );
    if (!target) {
      return [
        ...serverGroups,
        {
          key: 'other_files' as const,
          label: 'OTHER FILES',
          disabled: false,
          items: asItems,
        },
      ];
    }
    return serverGroups.map((group: AttachableGroup) =>
      group.key === 'other_files'
        ? { ...group, items: [...group.items, ...asItems] }
        : group
    );
  }, [data, pendingUploads]);

  // Memoised because consumers key render work off this object's identity: the
  // approval step feeds the picker into the editor's `toolbarEndSlot`, and a
  // fresh object every render meant a fresh slot element every render, which
  // defeated the editor's own memo and remounted the whole toolbar on each
  // keystroke and query tick. A toolbar unmounting under an open tooltip is
  // what left orphaned tooltips floating over the card.
  return useMemo(
    () => ({
      groups,
      isLoading,
      isError,
      refetch,
      isScorecardGenerating,
      isDraftSelected,
      toggleDraft,
      addUploadedToDraft,
      resetDraft,
      confirmDraft,
      commitUploaded,
      isSaving,
      selectedAttachments,
      draftAttachments,
      removeConfirmed,
      uploadingFile,
      setUploadingFile,
    }),
    [
      groups,
      isLoading,
      isError,
      refetch,
      isScorecardGenerating,
      isDraftSelected,
      toggleDraft,
      addUploadedToDraft,
      resetDraft,
      confirmDraft,
      commitUploaded,
      isSaving,
      selectedAttachments,
      draftAttachments,
      removeConfirmed,
      uploadingFile,
    ]
  );
};
